import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from 'wouter';

export default function TestLoaEmailPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const [, navigate] = useLocation();

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  if (!user) {
    return <div>Redirecting to login...</div>;
  }

  const sendTestLoaEmail = async () => {
    if (!email.includes('@')) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch('/api/test/loa-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: email }),
      });

      const data = await response.json();
      setResult(data);

      if (data.success) {
        toast({
          title: 'Test LOA Email Sent',
          description: `Email was sent to ${email}. Check your inbox or spam folder.`,
        });
      } else {
        toast({
          title: 'Failed to Send Email',
          description: data.message || 'An error occurred while sending the test email',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-10">
      <h1 className="text-2xl font-bold mb-6">Test LOA Email Delivery</h1>
      <p className="mb-4 text-muted-foreground">
        This tool allows you to test the delivery of Letter of Authority emails using the exact same
        implementation as the diagnostic emails that successfully reach Outlook inboxes.
      </p>

      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Send Test LOA Email</CardTitle>
          <CardDescription>
            Enter an email address to receive a test LOA email with a signing link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => window.history.back()}>
            Back
          </Button>
          <Button onClick={sendTestLoaEmail} disabled={loading}>
            {loading ? 'Sending...' : 'Send Test LOA Email'}
          </Button>
        </CardFooter>
      </Card>

      {result && (
        <Card className="w-full max-w-2xl mx-auto mt-8">
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-md overflow-auto">
              {JSON.stringify(result, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
        <h3 className="font-semibold text-blue-800">How This Works</h3>
        <p className="text-blue-700 mt-2">
          This test tool uses the exact same SendGrid implementation as the diagnostic email tool
          that successfully delivers to Outlook inboxes. It includes the same message formatting,
          headers, and attachment configuration that has been proven to work with Outlook.
        </p>
      </div>
    </div>
  );
}
